<template>
  <div class="list-header">
    <div class="list-header__left">
      <div class="list-header__icon">
        <div class="list-header__icon-grid">
          <div class="list-header__icon-cell"></div>
          <div class="list-header__icon-cell"></div>
          <div class="list-header__icon-cell"></div>
          <div class="list-header__icon-cell"></div>
        </div>
      </div>
      <div class="list-header__branding">
        <div class="list-header__hospital">{{ hospitalName }}</div>
        <div class="list-header__title">Virtual Nurse</div>
      </div>
    </div>

    <div class="list-header__right">
      <v-btn variant="text" class="list-header__settings-btn" icon size="small" @click="handleSettings">
        <v-icon size="20" color="#000000">mdi-cog-outline</v-icon>
        <v-tooltip activator="parent" location="bottom">Settings</v-tooltip>
      </v-btn>

      <v-btn variant="text" class="list-header__logout-btn" icon size="small" @click="showLogoutDialog = true">
        <v-icon size="20" color="#000000">mdi-logout</v-icon>
        <v-tooltip activator="parent" location="bottom">Logout</v-tooltip>
      </v-btn>

      <div class="list-header__user">
        <div class="list-header__avatar">
          {{ userInitials }}
        </div>
        <div class="list-header__user-name">{{ userName }}</div>
      </div>
    </div>

    <v-dialog v-model="showLogoutDialog" max-width="400">
      <v-card>
        <v-card-title class="logout-dialog__title">
          Confirm Logout
        </v-card-title>
        <v-card-text class="logout-dialog__text">
          Are you sure you want to log out?
        </v-card-text>
        <v-card-actions class="logout-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showLogoutDialog = false">
            Cancel
          </v-btn>
          <v-btn color="#6B46C1" variant="flat" @click="confirmLogout">
            Logout
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";

interface Props {
  hospitalName?: string;
  userName?: string;
}

interface Emits {
  (e: "settings"): void;
  (e: "logout"): void;
}

const props = withDefaults(defineProps<Props>(), {
  hospitalName: "Community Hospital",
  userName: "Alex Rodriguez",
});

const emit = defineEmits<Emits>();

const showLogoutDialog = ref(false);

const userInitials = computed(() => {
  const names = props.userName.split(" ");
  if (names.length >= 2) {
    return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
  }
  return props.userName.substring(0, 2).toUpperCase();
});

const handleSettings = () => {
  emit("settings");
};

const confirmLogout = () => {
  showLogoutDialog.value = false;
  emit("logout");
};
</script>

<style lang="scss" scoped>
.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  min-height: 72px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
  }
}

.list-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.list-header__icon {
  width: 32px;
  height: 32px;
  background: #2b7fff;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.list-header__icon-grid {
  display: grid;
  grid-template-columns: repeat(2, 6px);
  grid-template-rows: repeat(2, 6px);
  gap: 2px;
}

.list-header__icon-cell {
  width: 6px;
  height: 6px;
  background: #ffffff;
  border-radius: 1px;
}

.list-header__branding {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.list-header__hospital {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.333em;
  color: rgba(0, 0, 0, 0.6);
}

.list-header__title {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5em;
  color: #000000de;
}

.list-header__right {
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    width: 100%;
    justify-content: space-between;
  }
}

.list-header__settings-btn {
  color: #000000;

  :deep(.v-icon) {
    color: #000000 !important;
  }
}

.list-header__logout-btn {
  color: #000000;

  :deep(.v-icon) {
    color: #000000 !important;
  }
}

.list-header__user {
  display: flex;
  align-items: center;
  gap: 12px;
}

.list-header__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6b46c1 0%, #9d7ce3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #ffffff;
}

.list-header__user-name {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5em;
  color: #000000de;

  @media (max-width: 599px) {
    display: none;
  }
}

.logout-dialog__title {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 18px;
  padding: 20px 24px 12px;
}

.logout-dialog__text {
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  padding: 0 24px 20px;
}

.logout-dialog__actions {
  padding: 12px 16px;
}
</style>
